import { createApi } from "@reduxjs/toolkit/query/react";
import { MFOrderItem } from "Pages/MutualFundOrders/MutualFunds/useMFOrders";
import { setMFPortfolios } from "store/slices/mfOrdersSlice";
import { customBaseQuery } from "store/utils";

export type MFOrderTypeParam = "purchase" | "redemption" | "switch";

export type MFTransactionTypeParam = "lumpsum" | "sip" | "swp" | "stp";

export type MFFrequencyParam =
  | "monthly"
  | "quarterly"
  | "half_yearly"
  | "yearly";

export type MFPaymentTypeParam = "netbanking" | "upi" | "mandate";

export interface PortfolioAllocationParams {
  gcode: string;
  orderType: MFOrderTypeParam;
  portfolio_group?: number[];
  portfolio_value?: number[];
}

export interface PortfolioAllocationResponse {
  data: {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results: MFOrderItem[];
  };
  message?: string;
  status?: number;
}

export interface PlaceMFOrderItem {
  portfolioId: number;
  folioNumber: string | null;
  amount: string | null;
  units: string | null;
  redemptionType: "all" | null;
  switchType: "all" | null;
  paymentType: MFPaymentTypeParam | null;
  mandateId: string | null;
  sourceRefId: string | null;
  initiatedBy: string | null;
}

export interface PlaceMFOrderPayload {
  assetClass: "mf";
  orderType: MFOrderTypeParam;
  transactionType: MFTransactionTypeParam;
  gcode: string;
  switchInGcode: string | null;
  frequency: MFFrequencyParam | null;
  dayOfMonth: number | null;
  dayOfWeek: number | null;
  installments: number | null;
  isPerpetual: boolean;
  startDate: string | null;
  scheduledFor: string | null;
  rationale: string;
  sendForExecution: boolean;
  portfolioIds: number[];
  whatsappPortfolios: number[];
  items: PlaceMFOrderItem[];
}

export interface PlaceMFOrderResponse {
  data?: any;
  message?: string;
  status?: number;
}

export type OrderBatchStatus =
  | "in_process"
  | "completed"
  | "failed"
  | "partially_completed"
  | "cancelled";

export interface OrderBatchItem {
  id: number;
  batchId: number;
  portfolioId: number;
  clientId: number;
  clientName?: string | null;
  portfolioCode?: string | null;
  folioNumber: string | null;
  amount: string | null;
  units: string | null;
  nav: string | null;
  navSwitchOut?: string | null;
  navSwitchIn?: string | null;
  newFolioNumber?: string | null;
  unitsAllotted: string | null;
  netAmount: string | null;
  transactionDate: string | null;
  status: OrderBatchStatus;
  redemptionType?: "all" | null;
  switchType?: "all" | null;
  errorCode: string | null;
  errorMessage: string | null;
  [key: string]: any;
}

export interface OrderBatchRow {
  id: number;
  companyId: number;
  createdById: number;
  orderType: MFOrderTypeParam;
  transactionType: MFTransactionTypeParam;
  gcode: string;
  switchInGcode: string | null;
  frequency: MFFrequencyParam | null;
  dayOfMonth: number | null;
  installments: number | null;
  isPerpetual: boolean;
  startDate: string | null;
  scheduledFor: string | null;
  status: OrderBatchStatus;
  itemCount: number;
  successCount: number;
  failureCount: number;
  rationale: string;
  createdAt: string;
  updatedAt: string;
  items?: OrderBatchItem[];
}

export interface OrderBatchesResponse {
  totalCount: number;
  data: OrderBatchRow[];
}

export type OrderBatchDetailResponse = OrderBatchRow;

export type SystematicPlanStatus = "active" | "paused" | "cancelled";

export interface SystematicPlanRow {
  id: number;
  batchId: number;
  createdAt: string;
  portfolioId: number;
  portfolioName?: string;
  portfolioCode?: string;
  clientId?: number;
  schemeName?: string;
  gcode: string;
  switchInGcode?: string | null;
  folioNumber: string | null;
  planType: "sip" | "swp" | "stp";
  orderType: MFOrderTypeParam;
  amount: string;
  mandateId: string | null;
  frequency: MFFrequencyParam | null;
  dayOfMonth: number | null;
  installmentsTarget: number | null;
  installmentsRun: number | null;
  isPerpetual: boolean;
  startDate: string | null;
  fpOrderId: string | null;
  nextInstallmentDate?: string | null;
  lastInstallmentDate?: string | null;
  status: SystematicPlanStatus;
}

export interface SystematicPlansResponse {
  totalCount: number;
  data: SystematicPlanRow[];
}

export interface SystematicPlanInstallment {
  id: number;
  parentItemId: number;
  isInstallment: true;
  installmentNumber: number;
  fpOrderId: string | null;
  folioNumber: string | null;
  amount: string | null;
  units: string | null;
  unitsAllotted: string | null;
  nav: string | null;
  netAmount: string | null;
  transactionDate: string | null;
  status: OrderBatchStatus;
}

export interface SystematicPlanCompletion {
  itemId: number;
  planType: "sip" | "swp" | "stp";
  completionReason: "completed" | "cancelled" | "expired";
  totalInstallmentsRun: number;
  totalAmount: string | null;
  totalUnits: string | null;
  snapshot: Record<string, any>;
  completedAt: string;
}

export interface SystematicPlanDetail extends SystematicPlanRow {
  errorCode?: string | null;
  errorMessage?: string | null;
  installments: SystematicPlanInstallment[];
  completion?: SystematicPlanCompletion | null;
}

export interface Params {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const MFOrders = createApi({
  reducerPath: "MFOrders",
  baseQuery: customBaseQuery,
  tagTypes: [
    "PortfolioAllocation",
    "OrderBatches",
    "OrderBatch",
    "SystematicPlans",
    "SystematicPlan",
  ],
  endpoints: (builder) => ({
    getPortfolioAllocation: builder.query<
      PortfolioAllocationResponse,
      PortfolioAllocationParams
    >({
      query: ({ gcode, orderType, portfolio_group, portfolio_value }) => {
        const params: Record<string, string> = {
          gcode,
          order_type: orderType,
        };
        if (portfolio_group && portfolio_group.length > 0) {
          params.portfolio_group = JSON.stringify(portfolio_group);
        }
        if (portfolio_value && portfolio_value.length === 2) {
          params.portfolio_value = JSON.stringify(portfolio_value);
        }
        return {
          url: "/api/orders/portfolio-allocation/",
          method: "GET",
          params,
        };
      },
      providesTags: (_result, _err, { gcode, orderType }) => [
        { type: "PortfolioAllocation", id: `${gcode}:${orderType}` },
      ],
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMFPortfolios(data?.data?.results ?? []));
        } catch {
          // request failed — leave slice untouched
        }
      },
    }),

    placeMFOrder: builder.mutation<PlaceMFOrderResponse, PlaceMFOrderPayload>({
      query: (body) => ({
        url: "/api/orders/place/",
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "PortfolioAllocation" },
        { type: "OrderBatches", id: "LIST" },
      ],
    }),

    getOrderBatches: builder.query<OrderBatchesResponse, Params>({
      query: (params) => ({
        url: "/api/orders/batches/",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...(result.data ?? []).map(({ id }) => ({
                type: "OrderBatch" as const,
                id,
              })),
              { type: "OrderBatches" as const, id: "LIST" },
            ]
          : [{ type: "OrderBatches" as const, id: "LIST" }],
    }),

    getOrderBatchById: builder.query<OrderBatchDetailResponse, number | string>(
      {
        query: (id) => ({
          url: `/api/orders/batches/${id}/`,
          method: "GET",
        }),
        providesTags: (_result, _err, id) => [{ type: "OrderBatch", id }],
      },
    ),

    getSystematicPlans: builder.query<SystematicPlansResponse, void>({
      query: () => ({
        url: "/api/orders/items/",
        method: "GET",
        params: { view: "plans" },
      }),
      transformResponse: (response: any): SystematicPlansResponse => {
        if (Array.isArray(response)) {
          return { totalCount: response.length, data: response };
        }
        if (Array.isArray(response?.data)) {
          return {
            totalCount: response.totalCount ?? response.data.length,
            data: response.data,
          };
        }
        if (Array.isArray(response?.results)) {
          return {
            totalCount: response.count ?? response.results.length,
            data: response.results,
          };
        }
        if (Array.isArray(response?.data?.results)) {
          return {
            totalCount: response.data.count ?? response.data.results.length,
            data: response.data.results,
          };
        }
        return { totalCount: 0, data: [] };
      },
      providesTags: (result) => {
        const rows = Array.isArray(result?.data) ? result.data : [];
        return [
          ...rows.map(({ id }) => ({
            type: "SystematicPlan" as const,
            id,
          })),
          { type: "SystematicPlans" as const, id: "LIST" },
        ];
      },
    }),

    getSystematicPlanById: builder.query<SystematicPlanDetail, number | string>(
      {
        query: (id) => ({
          url: `/api/orders/items/${id}/`,
          method: "GET",
        }),
        transformResponse: (response: any): SystematicPlanDetail => {
          if (response?.data && typeof response.data === "object") {
            return response.data as SystematicPlanDetail;
          }
          return response as SystematicPlanDetail;
        },
        providesTags: (_result, _err, id) => [{ type: "SystematicPlan", id }],
      },
    ),
  }),
});

export const {
  useGetPortfolioAllocationQuery,
  useLazyGetPortfolioAllocationQuery,
  usePlaceMFOrderMutation,
  useGetOrderBatchesQuery,
  useGetOrderBatchByIdQuery,
  useGetSystematicPlansQuery,
  useGetSystematicPlanByIdQuery,
} = MFOrders;
